import { USER_QUERY } from '@app/apollo/queries';
import { setAuthCookie } from './setCookie';

/**
 * Login helper that sets user in Apollo cache
 * Call this after successful login mutation
 */
export const loginUser = (apolloClient, userData, accessToken, rememberMe = false) => {
  try {
    // Set auth cookie
    setAuthCookie(accessToken, rememberMe);

    // Set user in Apollo cache - this will update ALL components using useUser()
    apolloClient.writeQuery({
      query: USER_QUERY,
      data: { me: userData }
    });

    console.log('✅ User logged in and cached');
    return true;
  } catch (error) {
    console.error('❌ Failed to login user:', error);
    return false;
  }
};

/**
 * Logout helper that clears user from Apollo cache
 */
export const logoutUser = (apolloClient) => {
  try {
    // Clear auth cookie
    document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';

    // Clear user from Apollo cache
    apolloClient.writeQuery({
      query: USER_QUERY,
      data: { me: null }
    });

    // Clear specific cache fields
    apolloClient.cache.evict({ fieldName: 'me' });

    // Clear entire cache (optional - removes all cached data)
    apolloClient.cache.reset();

    console.log('✅ User logged out and cache cleared');
    return true;
  } catch (error) {
    console.error('❌ Failed to logout user:', error);
    return false;
  }
};

/**
 * Update user profile in cache
 * Call this after successful profile update mutation
 */
export const updateUserProfile = (apolloClient, updatedUserData) => {
  try {
    // Get current user from cache
    const currentData = apolloClient.readQuery({
      query: USER_QUERY,
      errorPolicy: 'ignore'
    });

    if (currentData?.me) {
      // Merge updated data with existing user data
      const updatedUser = { ...currentData.me, ...updatedUserData };

      // Write back to cache
      apolloClient.writeQuery({
        query: USER_QUERY,
        data: { me: updatedUser }
      });

      console.log('✅ User profile updated in cache');
      return true;
    }

    return false;
  } catch (error) {
    console.error('❌ Failed to update user profile:', error);
    return false;
  }
};

/**
 * Check if user is authenticated by checking cache
 */
export const isUserAuthenticated = (apolloClient) => {
  try {
    const data = apolloClient.readQuery({
      query: USER_QUERY,
      errorPolicy: 'ignore'
    });
    return !!data?.me;
  } catch (error) {
    return false;
  }
};

/**
 * Get current user from cache without triggering network request
 */
export const getCurrentUser = (apolloClient) => {
  try {
    const data = apolloClient.readQuery({
      query: USER_QUERY,
      errorPolicy: 'ignore'
    });
    return data?.me || null;
  } catch (error) {
    return null;
  }
};
